import type { UploadedDocument } from '@/types/formField.types';
import type * as DocumentPicker from 'expo-document-picker';
import {
  FILE_FORMAT_MIME_MAP,
  FILE_TYPE_ICONS,
  type FileUploaderIconName,
} from './fileUploaderField.constants';

export const createFileId = (
  asset: DocumentPicker.DocumentPickerAsset,
): string =>
  `${asset.uri}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const toUploadedDocument = (
  asset: DocumentPicker.DocumentPickerAsset,
): UploadedDocument => ({
  id: createFileId(asset),
  uri: asset.uri,
  name: asset.name,
  size: typeof asset.size === 'number' ? asset.size : undefined,
  mimeType: asset.mimeType,
});

export const isFileTypeAllowed = (
  file: UploadedDocument,
  acceptedFormats: string[],
  acceptedMimeTypes: string[],
): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const formatSet = new Set(
    acceptedFormats.map((format) => format.toLowerCase()),
  );
  const extensionMatch = formatSet.has(extension);
  const mimeMatch =
    file.mimeType && acceptedMimeTypes.includes(file.mimeType.toLowerCase());

  return extensionMatch || Boolean(mimeMatch);
};

export const buildAcceptedMimeTypes = (formats: string[]): string[] =>
  Array.from(
    new Set(
      formats.flatMap(
        (format) => FILE_FORMAT_MIME_MAP[format.toLowerCase()] ?? [],
      ),
    ),
  );

export const formatFileSize = (size?: number): string => {
  if (!size || size <= 0) {
    return 'Unknown size';
  }

  if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${(size / 1024).toFixed(1)} KB`;
};

export const getIconName = (file: UploadedDocument): FileUploaderIconName => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return FILE_TYPE_ICONS[extension] ?? 'document-outline';
};
